import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { DesktopOnly, IconButton, MobileOnly } from '../../ds';
import { ExceptionItem, formatExceptionDate } from '../../schedules';
import { PencilIcon, TrashIcon } from '../icons';
import {
  CardActions, CardHeader, CardHeaderTitle, ExceptionCardBox, ExceptionCards, ExceptionHeaderRow, ExceptionRow,
  ExceptionTable, InfoGrid,
} from './styles';

export interface ExceptionView extends ExceptionItem {
  title: string;
}

interface ExceptionsListProps {
  exceptions: ExceptionView[];
  actions: { onEdit: (item: ExceptionView) => void; onRemove: (item: ExceptionView) => void; disabled: boolean } | null;
}

/** Exceções em tabela no desktop e em cards no celular, como no design. */
export function ExceptionsList({ exceptions, actions }: Readonly<ExceptionsListProps>): ReactElement {
  const { t } = useTranslation();
  const permissionLabel = (item: ExceptionView): string => t(item.permission === 'allow' ? 'ecoSimplificadoPermitir' : 'ecoSimplificadoBloquearCurto');

  function renderActions(item: ExceptionView): ReactElement | null {
    if (!actions) return null;
    const editLabel = t('ecoSimplificadoEditarExcecaoNome', { titulo: item.title, interpolation: { escapeValue: false } });
    const removeLabel = t('ecoSimplificadoExcluirExcecaoNome', { titulo: item.title, interpolation: { escapeValue: false } });
    return (
      <CardActions>
        <IconButton type="button" title={editLabel} aria-label={editLabel} disabled={actions.disabled} onClick={() => actions.onEdit(item)}>
          <PencilIcon size={17} />
        </IconButton>
        <IconButton type="button" $danger title={removeLabel} aria-label={removeLabel} disabled={actions.disabled} onClick={() => actions.onRemove(item)}>
          <TrashIcon size={17} />
        </IconButton>
      </CardActions>
    );
  }

  return (
    <>
      <DesktopOnly>
        <ExceptionTable>
          <ExceptionHeaderRow>
            <span>{t('ecoSimplificadoTitulo')}</span>
            <span>{t('ecoSimplificadoData')}</span>
            <span>{t('ecoSimplificadoRepetirTodoAno')}</span>
            <span>{t('funcionamento')}</span>
            <span>{t('inicio')}</span>
            <span>{t('fim')}</span>
            <span />
          </ExceptionHeaderRow>
          {exceptions.map((item) => (
            <ExceptionRow key={item.date} data-testid="eco-simplificado-linha-excecao">
              <span>{item.title}</span>
              <span>{formatExceptionDate(item.date)}</span>
              <span>{t('nao')}</span>
              <span>{permissionLabel(item)}</span>
              <span>{item.start}</span>
              <span>{item.end}</span>
              <span>{renderActions(item)}</span>
            </ExceptionRow>
          ))}
        </ExceptionTable>
      </DesktopOnly>
      <MobileOnly>
        <ExceptionCards>
          {exceptions.map((item) => (
            <ExceptionCardBox key={item.date}>
              <CardHeader>
                <CardHeaderTitle>{item.title}</CardHeaderTitle>
                {renderActions(item)}
              </CardHeader>
              <InfoGrid $compact>
                <span>{t('ecoSimplificadoData')}</span>
                <span>{formatExceptionDate(item.date)}</span>
                <span>{t('ecoSimplificadoRepetirTodoAno')}</span>
                <span>{t('nao')}</span>
                <span>{t('funcionamento')}</span>
                <span>{permissionLabel(item)}</span>
                <span>{t('inicio')}</span>
                <span>{item.start}</span>
                <span>{t('fim')}</span>
                <span>{item.end}</span>
              </InfoGrid>
            </ExceptionCardBox>
          ))}
        </ExceptionCards>
      </MobileOnly>
    </>
  );
}
